import { Injectable } from '@angular/core';
import { SyncCryptService } from '../core/crypt/sync-crypt.service';
import { LoggerService } from '../core/logger.service';
import { UserService } from '../core/user.service';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FILE_TYPE } from '../shared/models/file-type.model';
import { Store, select } from '@ngrx/store';
import * as fromRoot from '../reducers';
import { User } from '../shared/models/user.model';
import mimeData from '../../mimeData';
import { IFile, IFileTypeMime, IFileTypes, IShareKey } from 'src/sync';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from '../core/api.service';
import { FileItem, PermissionActions } from '../shared/models';
import { CONSTANTS, FileItemType, PreviewType } from '../shared/models/enums';
import * as musicMetadata from 'music-metadata';
import { BrowserService } from './browser.service';
import { TextEditorVariant, TextFileTypeService } from '../shared/services/text-file-type.service';
import { NameNormalizeService } from '../shared/services/name-normalize.service';

@Injectable({
  providedIn: 'root',
})
export class FileItemService {
  public MAX_PREVIEW_SIZE = 10485760 * 4;
  public MAX_PDF_PREVIEW_SIZE = 10485760 * 10;
  public MAX_PREVIEW_SIZE_XLS = 10485760;
  private FileTypes: IFileTypes = FILE_TYPE;
  public user: User;

  public fileItemSubject = new BehaviorSubject<any | null>(null);
  public fileItem$ = this.fileItemSubject.asObservable();

  private fileMimeType = {
    txt: 'text/plain',
  };

  constructor(
    private userService: UserService,
    private loggerService: LoggerService,
    private syncCryptService: SyncCryptService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private store: Store<fromRoot.State>,
    private apiService: ApiService,
    private browserService: BrowserService,
    private textFileTypeService: TextFileTypeService,
    private nameNormalize: NameNormalizeService,
  ) {
    this.init();
  }

  public init(): void {
    this.store.pipe(select(fromRoot.getAuthUser)).subscribe((data) => {
      if (data) {
        this.user = data;
      } else {
        this.user = null;
      }
    });
  }

  public async decryptName(item: IFile, sharekeys?: IShareKey): Promise<string> {
    if (item.name) {
      return item.name;
    }
    if (item.enc_name) {
      return this.syncCryptService.filenameDecrypt(item.enc_name, '');
    }
    const encryptedShareKey = sharekeys?.[item.share_key_id];
    if (!encryptedShareKey) {
      throw new Error('No share key found for ' + item.share_key_id);
    }
    const sharekey = await this.syncCryptService.sharekeyDecrypt(encryptedShareKey, item.share_key_id);
    return this.syncCryptService.filenameDecrypt(item.enc_share_name, sharekey);
  }

  public getFileTypeData(fname: string): IFileTypeMime {
    const extension = fname ? fname.split('.').pop() : '',
      ext = extension.toLowerCase();
    let data: IFileTypeMime = {
        viewable: false,
        editable: false,
        unicode: false,
        icon: 'file',
        cssclass: 'mime-unknown',
        kind: extension.toLowerCase(),
      },
      category: any = {},
      cat: string,
      fileext: string;

    if (extension === fname) {
      data.kind = 'unknown';
      return data; // return a default value if file has no extension
    }
    for (const keyCat in this.FileTypes) {
      const catVal = this.FileTypes[keyCat];
      for (const keyExt in catVal.extensions) {
        if (keyExt === ext) {
          cat = keyCat;
          fileext = keyExt;
          break; // break out of inner loop
        }
      }
      if (fileext !== undefined) {
        break; // break out of loop
      }
    }

    if (fileext !== undefined) {
      category = this.FileTypes[cat];
      data = Object.assign(data, category, category.extensions[fileext]);
      data.extensions = null;
    }

    return data;
  }

  public isPDF(item: IFile): boolean {
    const name = item.name;
    const ext = name.substr(name.lastIndexOf('.') + 1).toLowerCase();

    return ext === 'pdf' && ext != name.toLowerCase();
  }
  public isImage(item: IFile): boolean {
    const name = item.name;
    const ext = name.substr(name.lastIndexOf('.') + 1).toLowerCase();
    let canPreview = false;
    switch (ext) {
      case 'jpg':
      case 'png':
      case 'jpeg':
      case 'gif':
      case 'svg':
        canPreview = true;
        break;
      case 'heic':
      case 'heif':
      case 'webp':
      case 'avif':
      case 'tif':
      case 'tiff':
        canPreview = item.has_thumb2 != 0;
        break;
      default:
        canPreview = false;
    }
    return canPreview && ext != name.toLowerCase();
  }
  public isText(item: IFile): boolean {
    return this.textFileTypeService.isTextFile(item);
  }

  public getTextEditorVariant(item: IFile): TextEditorVariant {
    return this.textFileTypeService.getTextEditorVariant(this.getFileExt(item.name));
  }

  // https://help.accusoft.com/PrizmDoc/v13.5/HTML/webframe.html#supported-file-formats.html
  public isDocument(item: IFile): boolean {
    const name = item.name;
    const ext = name.substr(name.lastIndexOf('.') + 1).toLowerCase();
    let canPreview = false;

    // PDF is left out of this switch as it's handled on it's own.
    switch (ext) {
      case 'odt': // open office doc
      case 'ott':
      case 'fodt':
      case 'odf': // openoffice math formula
      case 'ods': // openoffice spreadsheet
      case 'ots':
      case 'fods':
      case 'odp': // openoffice presentation
      case 'otp':
      case 'fodp':
      case 'odg': // openoffice drawing
      case 'otg':
      case 'fodg':
      case 'rtf':
      case 'sxw':

      case 'doc': // ms word
      case 'dot':
      case 'docx':
      case 'dotx':
      case 'docm':
      case 'dotm':

      case 'ppt': // ms power point
      case 'pot':
      case 'pps':
      case 'pptm':
      case 'potx':
      case 'potm':
      case 'ppsx':
      case 'ppsm':
      case 'pptx':

      case 'xls': // ms excel
      case 'xlt':
      case 'xlsx':
      case 'xlsm':
      case 'xltx':
      case 'xltm':

      case 'vsd': // ms visio
      case 'vsdx':
      case 'vsdm':
      case 'vdx':

      case 'rtf': // generic documents
      // case 'csv':

      case 'eml': // email formats
      case 'msg':

      case 'dwg': // autocad formats
      case 'dwf':
      case 'dxf':
      // case 'dgn': // doesn't seem to work too well

      case 'dcm': // medical format
      case 'dicom':
      case 'dcim':
      case 'dicm':

      case 'psd': // image formats
      case 'ras':
      case 'cal':
      case 'cals':
      case 'dib':
      case 'bmp':
      case 'ico':
      case 'pbm':
      case 'pgm':
      case 'ppm':
      case 'img':
      case 'cur':
      case 'ncr':
      case 'sgi':
      case 'pct':
      case 'pcx':
      case 'pcd':
      case 'dcx':
      case 'wmf':
      case 'emf':
      case 'tga':
      case 'tpic':
        canPreview = true;
        break;

      default:
        canPreview = false;
    }
    return canPreview && ext != name.toLowerCase();
  }

  public isOfficeLinkEdit(
    item: IFile,
    isUploadAllowed: number,
    isFileEditAllowed: number,
    isCwdShared: number,
  ): boolean {
    return (
      this.isMSOffice(item, 'edit') &&
      this.user &&
      this.user.exists &&
      isUploadAllowed === 1 &&
      isFileEditAllowed === 1 &&
      isCwdShared === 0
    );
  }

  public isOfficeDocument(fileItem: IFile): boolean {
    const msOfficeExtensions = [
      'csv',
      'ods',
      'xls',
      'xlsb',
      'xlsm',
      'xlsx', // Excel
      'odp',
      'pot',
      'potm',
      'potx',
      'pps',
      'ppsm', // PowerPoint
      'ppsx',
      'ppt',
      'pptm',
      'pptx',
      'wopitest',
      'wopitestx', // WopiTest
      'doc',
      'docm',
      'docx',
      'dot',
      'dotm',
      'dotx', // Word
      'odt',
      'rtf',
    ];

    return msOfficeExtensions.includes(fileItem.file_extension);
  }

  public isMSOffice(item: IFile, action: string): boolean {
    const application = this.getMSOfficeApp(item);
    const maxSize = {
      Excel: 5242880, // 5MB = 5x2^20
      Word: 52428800, // 50MB
      PowerPoint: 104857600, // 100MB, Real Limit is 300MB
      WopiTest: 10485760, // 10MB
    };

    if (application === 'None' || !this.isMSActionAllowed(item, action)) {
      return false;
    } else if (maxSize[application] === undefined || maxSize[application] < item.size) {
      return false;
    } else {
      return true;
    }
  }

  public getMSOfficeApp(item: IFile): 'Excel' | 'PowerPoint' | 'WopiTest' | 'Word' | 'None' {
    const ext = item.name.split('.').pop();

    switch (ext) {
      case 'csv':
      case 'ods':
      case 'xls':
      case 'xlsb':
      case 'xlsm':
      case 'xlsx':
        return 'Excel';

      case 'odp':
      case 'pot':
      case 'potm':
      case 'potx':
      case 'pps':
      case 'ppsm':
      case 'ppsx':
      case 'ppt':
      case 'pptm':
      case 'pptx':
        return 'PowerPoint';

      case 'doc':
      case 'docm':
      case 'docx':
      case 'dot':
      case 'dotm':
      case 'dotx':
      case 'odt':
      case 'rtf':
        return 'Word';

      case 'wopitest':
      case 'wopitestx':
        return 'WopiTest';

      default:
        return 'None';
    }
  }

  public isMSActionAllowed(item: IFile, action: string): boolean {
    const ext = this.getFileExt(item.name);

    // Note: These are most available actions defined by wopiDiscovery c. 12/2019.
    // Many of these are not used, for example we don't allow any Onenote or Visio files
    // as they're not defined above in this.getMSOfficeApp().

    // Other actions, such as convert and editnew, are defined here but we don't
    // have support for them within WOPI
    const supportedActions = {
      edit: ['docm', 'docx', 'odt', 'odp', 'ppsx', 'pptx', 'ods', 'xlsb', 'xlsm', 'xlsx', 'wopitest', 'wopitestx'],
      embedview: [
        'doc',
        'docm',
        'docx',
        'dot',
        'dotm',
        'dotx',
        'odt',
        'rtf',
        'odp',
        'pot',
        'potm',
        'potx',
        'pps',
        'ppsm',
        'ppsx',
        'ppt',
        'pptm',
        'pptx',
        'ods',
        'xlsb',
        'xlsm',
        'xlsx',
      ],
      view: [
        'doc',
        'docm',
        'docx',
        'dot',
        'dotm',
        'dotx',
        'odt',
        'rtf',
        'odp',
        'pot',
        'potm',
        'potx',
        'pps',
        'ppsm',
        'ppsx',
        'ppt',
        'pptm',
        'pptx',
        'csv',
        'ods',
        'xls',
        'xlsb',
        'xlsm',
        'xlsx',
        'wopitest',
        'wopitestx',
      ],
    };
    return supportedActions[action] && supportedActions[action].includes(ext);
  }

  public isPreviewable(item: IFile): boolean {
    if (this.isImage(item) || this.isVideo(item) || this.isAudio(item)) {
      return true;
    }
    if (
      (this.getFileExt(item.name) == 'xls' || this.getFileExt(item.name) == 'xlsx') &&
      item.size > this.MAX_PREVIEW_SIZE_XLS
    ) {
      return false;
    }
    if (this.isPDF(item) && item.size <= this.MAX_PDF_PREVIEW_SIZE) {
      return true;
    }
    if (item.size > this.MAX_PREVIEW_SIZE) {
      return false;
    }
    return true;
  }

  public bytesToSize(bytes: number): string {
    if (!bytes && bytes != 0) {
      return '';
    }
    const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    if (bytes == 0) {
      return '0 bytes';
    }
    const x = Math.log(bytes) / Math.log(1024);
    const i: number = Math.floor(x);
    const formattedNumber = bytes / Math.pow(1024, i);
    if (i === 0) {
      return this.decimalPipe.transform(formattedNumber, '1.0', 'en-US') + ' ' + sizes[i];
    }
    return this.decimalPipe.transform(parseFloat(formattedNumber.toFixed(1)), '1.0', 'en-US') + ' ' + sizes[i];
  }

  public decorate(item: IFile): IFile {
    item.search_name = this.nameNormalize.toNfcLower(item.name);
    item.storage_backend = item.storage_backend || CONSTANTS.DEFAULT_STORAGE_BACKEND;
    item.usertime = item.usertime ? item.usertime : 0;
    item.date_medium = this.datePipe.transform(item.usertime, 'MMM d, y h:mm a');
    item.filesize = this.bytesToSize(item.size);
    item.active = false;
    item.context = 'files';
    item.compat = false;
    item.file_extension = this.getFileExt(item.name);
    item.mime_type = this.getmimeType(this.getFileExt(item.name));
    if (item.synctype) {
      item.type = (item.synctype & 1) === 1 ? FileItemType.DIR : FileItemType.FILE;
      item.is_dir = (item.synctype & 1) === 1 ? 1 : 0;
      item.is_readonly = (item.synctype & 2) === 2 ? 0 : 1;
      item.is_hide_share = (item.synctype & 4) === 4 ? 1 : 0;
      item.is_app_share = (item.synctype & 8) === 8 ? 1 : 0;
      item.is_shared = (item.synctype & 16) === 16 ? 1 : 0;
      item.is_publink = (item.synctype & 32) === 32 ? 1 : 0;
      item.is_share_root = (item.synctype & 128) === 128 && item.is_shared ? 1 : 0;
    }

    if (item.type === FileItemType.DIR) {
      // hardcode this here  because on publinks, there is no synctype
      item.is_dir = 1;
      let cssclass = '';
      if (item.is_deleted) {
        cssclass = 'dir-deleted';
      } else {
        if (item.is_share_root) {
          if (item.is_app_share) {
            if (item.is_hide_share) {
              cssclass = 'dir-app';
            } else {
              cssclass = 'dir-app-user';
            }
          } else {
            if (item.is_hide_share) {
              cssclass = 'dir';
            } else {
              cssclass = 'dir-sync';
            }
          }
        } else {
          cssclass = 'dir';
        }
      }
      item.cssclass = cssclass;

      item.kind = item.is_deleted ? 'deleted folder' : item.is_share_root ? 'shared folder' : 'folder';
    } else if (item.type === FileItemType.FILE) {
      item.is_dir = 0;
      item.filetype = this.getFileTypeData(item.name);
      item.kind = item.is_deleted ? 'deleted file' : item.filetype?.kind;
      item.icon = item.filetype?.icon;
      item.cssclass = item.is_deleted ? 'file-deleted' : item.filetype?.cssclass;
    } else {
      this.loggerService.error('Unknown type for ' + item.sync_id);
    }
    item.imgsrc = 'assets/icons/' + item.icon + '.svg';
    return item;
  }

  /**
   * Formats the API result for a path item
   */
  public async format(item: any, sharekeys?: IShareKey): Promise<IFile> {
    const clonedFileItem = { ...item };

    if (clonedFileItem.sync_id == this.userService.get('web_sync_id')) {
      clonedFileItem.name = 'Vault';
    } else if (clonedFileItem.sync_id == this.userService.get('syncus_sync_id')) {
      clonedFileItem.name = 'Sync';
    }

    try {
      const fname = await this.decryptName(clonedFileItem, sharekeys);
      if (fname !== undefined && fname === '') {
        clonedFileItem.name = 'SYNCERROR - File name is empty, try renaming - ' + clonedFileItem.sync_id;
        this.loggerService.error(`file ${clonedFileItem.sync_id} was uploaded with an empty name, try renaming it`);
      } else {
        clonedFileItem.name = fname;
      }
    } catch (e) {
      this.loggerService.error(`Error decrypting name for ${clonedFileItem.sync_id}`);
      clonedFileItem.name = 'SYNCERROR - File decryption failed - ' + clonedFileItem.sync_id;
      this.loggerService.error('Could not decrypt file name');
      this.loggerService.error(`Sync ID: ${clonedFileItem.sync_id}`);
      this.loggerService.error(`enc_name: ${clonedFileItem.enc_name}`);
    }

    return this.decorate(clonedFileItem);
  }

  /**
   * function get file extension from file name
   * @param fileName
   * @returns
   */
  public getFileExt(fileName: string): string {
    if (fileName) {
      const tmp = fileName.split('.');
      if (tmp.length > 1) {
        return tmp.pop();
      }
    }
    return '';
  }

  public isStreamable(fileType: string): boolean {
    if (['video', 'audio', 'webm', 'mp3', 'asf'].includes(fileType)) {
      return true;
    }
    return false;
  }

  public getmimeType(extName: string): string {
    const data = mimeData.find((item) => item.types.includes(extName.includes('.') ? extName : `.${extName}`));
    if (data) {
      return data.name ? data.name : 'application/octet-stream';
    } else {
      return 'application/octet-stream';
    }
  }

  public isVideo(item: IFile): boolean {
    const name = item.name;
    const ext = name.substr(name.lastIndexOf('.') + 1).toLowerCase();
    let supportedFileType = false;

    switch (ext) {
      case 'mp4':
        supportedFileType = true;
        break;
      case '3gp':
        if (item.context == 'link' || item.context == 'applink') {
          return !this.browserService.isFirefox();
        }
        break;
      case 'm4v':
      case 'webm':
        if (item.context == 'link' || item.context == 'applink') {
          supportedFileType = true;
        }
        break;
      case 'mov':
        supportedFileType = this.browserService.checkMovSupport();
        break;
      case 'm4p':
      case 'ogg':
      case 'mkv':
      case 'ogv':
      case 'mpg':
      case 'mpeg':
      case 'wmv':
      case 'asf':
      case 'flv':
      case 'avi':
        supportedFileType = false;
        break;
      default:
        supportedFileType = false;
    }

    return supportedFileType && ext != name.toLowerCase();
  }

  public isAudio(item: IFile): boolean {
    const name = item.name;
    const ext = name.substr(name.lastIndexOf('.') + 1).toLowerCase();
    let supportedFileType = false;

    switch (ext) {
      case 'mp3':
        supportedFileType = true;
        break;
      case 'wav':
      case 'aac':
        supportedFileType = item.context === 'link' || item.context === 'applink';
        break;
      default:
        supportedFileType = false;
    }

    return supportedFileType && ext != name.toLowerCase();
  }

  public isLikelyMediaFile(name: string): boolean {
    const ext = name.substr(name.lastIndexOf('.') + 1).toLowerCase();
    return [
      'mp4',
      'm4v',
      'm4p',
      'ogg',
      'mov',
      'mkv',
      'webm',
      '3gp',
      'ogv',
      'mpg',
      'mpeg',
      'wmv',
      'asf',
      'flv',
      'avi',
      'mp3',
      'wav',
      'aac',
    ].includes(ext);
  }

  public previewFileType(item: FileItem): PreviewType {
    let type: PreviewType = PreviewType.DEFAULT;
    if (this.isImage(item)) {
      type = PreviewType.IMAGE;
    } else if (this.isText(item)) {
      type = PreviewType.TEXT;
    } else if (this.isPDF(item) && (item.context === 'link' || item.context === 'applink') && item.previewonly) {
      type = PreviewType.PDF;
    } else if (this.isPDF(item) && item.context === 'files') {
      type = PreviewType.PDF;
    } else if (this.displayOffice(item)) {
      type = PreviewType.OFFICE;
    } else if (this.displayDoc(item)) {
      type = PreviewType.DOC;
    } else if (this.isVideo(item)) {
      type = PreviewType.VIDEO;
    } else if (this.isAudio(item)) {
      type = PreviewType.AUDIO;
    } else {
      type = PreviewType.DEFAULT;
    }
    return type;
  }

  public displayDoc(item: FileItem): boolean {
    return (
      this.isDocument(item) &&
      // only compat links
      ((item.compat && (item.context == 'link' || item.context == 'applink')) || item.context == 'files')
    );
  }

  public displayOffice(item: FileItem): boolean {
    return (
      this.isMSOffice(item, 'embedview') &&
      // Only enabled in files, for now
      item.context === 'files' &&
      this.user.is_office === 1
    );
  }

  public generateFileObj(item: any): { mimetype: string; extensions: string } {
    const extensions = this.getFileExt(item.name);
    if (this.isMSOffice(item, 'embedview')) {
      item.context = 'files';
    } else if (this.isDocument(item)) {
      item.context = 'true';
    }
    return { mimetype: this.fileMimeType[extensions], extensions };
  }
  public getFileTypeIcon(fileItem: any): string {
    if (fileItem.type === FileItemType.DIR) {
      return fileItem.is_share_root && !fileItem.is_hide_share ? 'folder-shared-team' : 'folder';
    } else {
      switch (fileItem?.file_extension?.toLowerCase?.()) {
        // Word document types
        case 'doc':
          return 'file-type-doc';
        case 'docx':
          return 'file-type-docx';
        case 'dot':
          return 'file-type-dot';
        case 'dotx':
          return 'file-type-dotx';
        case 'dotm':
          return 'file-type-dotm';
        case 'docm':
          return 'file-type-docm';
        case 'odt':
          return 'file-type-odt';

        // Excel spreadsheet types
        case 'xls':
          return 'file-type-xls';
        case 'xlsx':
          return 'file-type-xlsx';
        case 'xlsm':
          return 'file-type-xlsm';
        case 'xlsb':
          return 'file-type-xlsb';
        case 'ods':
          return 'file-type-ods';
        case 'csv':
          return 'file-type-csv';

        // PowerPoint presentation types
        case 'ppt':
          return 'file-type-ppt';
        case 'pptx':
          return 'file-type-pptx';
        case 'potx':
          return 'file-type-potx';
        case 'potm':
          return 'file-type-potm';
        case 'ppsx':
          return 'file-type-ppsx';
        case 'ppsm':
          return 'file-type-ppsm';
        case 'pptm':
          return 'file-type-pptm';
        case 'pot':
          return 'file-type-pot';
        case 'pps':
          return 'file-type-pps';
        case 'odp':
          return 'file-type-odp';

        case 'pdf':
          return 'file-type-pdf';

        // Image file types
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'svg':
        case 'heic':
        case 'heif':
        case 'webp':
        case 'avif':
        case 'tif':
        case 'tiff':
          return 'file-type-image';

        // Text-based file types
        case 'txt':
        case 'rtf':
        case 'md':
        case 'tsv':
        case 'xml':
        case 'json':
          return 'file-document';

        // Audio types
        case 'mp3':
        case 'wav':
        case 'ogg':
        case 'flac':
        case 'mpeg':
        case 'aac':
          return 'file-type-audio';

        // Video types
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'wmv':
        case 'flv':
        case 'mkv':
          return 'file-type-video';

        // Archive types
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
          return 'file-type-archive';

        // Code-related files
        case 'js':
        case 'html':
        case 'ts':
        case 'java':
        case 'cpp':
        case 'c':
        case 'h':
        case 'cs':
        case 'py':
        case 'css':
        case 'php':
        case 'sql':
        case 'sh':
          return 'file-type-code';

        // Adobe Illustrator files
        case 'ai':
          return 'file-type-illustrator';

        // Executable files
        case 'exe':
          return 'file-type-exe';

        // PostScript files
        case 'ps':
        case 'eps':
        // OpenXML formats
        case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'vnd.openxmlformats-officedocument.presentationml.presentation':
        case 'vnd.openxmlformats':
          return 'file-document';

        // Blank document icon for unknown file types
        case 'bin':
        case 'octet-stream':
        case 'cdimage':
        case 'vnd.ms-pki.stl':
          return 'file';

        default:
          return 'file';
      }
    }
  }

  public async fetchFileItem(locId: number): Promise<void> {
    try {
      const result = await this.apiService.execute('pathget', { sync_id: locId });
      if (result) {
        this.fileItemSubject.next(result);
      } else {
        console.warn('Received an invalid result from API');
        this.fileItemSubject.next(null);
      }
    } catch (error) {
      console.error('Failed to fetch file item', error);
      this.fileItemSubject.next(null);
    }
  }

  public hasPermission = (permissionKey: PermissionActions): boolean => {
    return !!this.userService.getPermssionByKey(permissionKey);
  };

  public async parseBlob(blob: Blob): Promise<musicMetadata.IAudioMetadata> {
    return musicMetadata.parseBlob(blob);
  }

  public generatePreviewLink(syncId: number, type: string): string {
    return `/file/${syncId}/view/${type}`;
  }
}
